import React, { ChangeEvent } from "react";
import moment_tz from "moment-timezone";
import { useSelector } from "react-redux";
import { Field, Form, Formik } from "formik";
import { Grid, Button, Box } from "@mui/material";
import { useDispatch } from "react-redux";

import PassersReport from "../PassersReport";
import { FormDatePicker } from "../../../../../components/ui";
import { FormInput, FormCheckbox } from "../../../../../components/ui";
import { SelectByDateWithCustomPeriod } from "../../../../../components/ui";

import { VALIDATION_SCHEMA } from "./constants";
import { DATE_FORMAT, DATES_VALUE } from "../../../../../constants";

import { usePassersContext } from "../../../../../context";
import { financialsActions } from "../../../../../reducers/financials";
import { countEmailsInString } from "../AccountsSalesTable/utils";

interface IProps {
  setEmails: (emails: string) => void;
  setReportTitleDate: (reportTitleDate: string) => void;
  onDateChange: (startDate: string, endDate: string, isAllTime: boolean | undefined) => void;
  setPeriodFilter: (period: { startDate: string; endDate: string }) => void;
}

const AccountsSearch: React.FC<IProps> = ({
  setEmails,
  setReportTitleDate,
  onDateChange,
  setPeriodFilter,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const { passersTab, setPassersTab } = usePassersContext();
  const { loading } = useSelector((state) => state.financials.passersStats);

  const onPeriodReportChange = (
    e: ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string | boolean) => void
  ) => {
    const { checked, name } = e.target;

    dispatch(financialsActions.clearPassersStatsState());
    dispatch(financialsActions.clearPassedSubscriptionPurchaseDateState());
    dispatch(financialsActions.clearSpentToPassEvaluationState());

    if (checked) {
      setEmails("");
      setReportTitleDate("");

      setFieldValue("emails", "");
      setFieldValue("report_title_date", "");
    } else {
      setPeriodFilter({ startDate: "", endDate: "" });
    }
    setFieldValue(name, checked);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Formik
          onSubmit={({ emails, report_title_date }, rest) => {
            setEmails(emails);
            setReportTitleDate(moment_tz(report_title_date).format(DATE_FORMAT));
            setPassersTab({
              ...passersTab,
              search: {
                ...passersTab.search,
                emails,
                report_title_date: moment_tz(report_title_date).format(DATE_FORMAT),
              },
            });

            rest.resetForm({
              values: {
                emails,
                report_title_date,
                use_date_range: false,
              },
            });
          }}
          validateOnMount
          initialValues={{
            emails: passersTab.search.emails,
            report_title_date: passersTab.search.report_title_date,
            use_date_range: false,
          }}
          validationSchema={VALIDATION_SCHEMA}
        >
          {({ isValid, values, setFieldValue }) => {
            const { use_date_range } = values;
            const emailsCounter = countEmailsInString(values.emails);
            const emailsLabel = emailsCounter ? `Emails (${emailsCounter})` : "Emails";

            return (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Field
                      name="use_date_range"
                      component={FormCheckbox}
                      label="Use period report"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onPeriodReportChange(e, setFieldValue)
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  {!use_date_range && (
                    <Grid item xs={5}>
                      <Field
                        name="emails"
                        component={FormInput}
                        placeholder="...@gmail.com, ...@gmail.com etc ..."
                        label={emailsLabel}
                        fullWidth
                        variant="outlined"
                        multiline
                        maxRows={11}
                      />
                    </Grid>
                  )}

                  <Grid item xs={7}>
                    <Box sx={{ display: "flex", alignItems: "space-between" }}>
                      {use_date_range && (
                        <SelectByDateWithCustomPeriod
                          setIsLoading={() => void null}
                          getReport={onDateChange}
                          setReport={() => void null}
                          initialDateBy={DATES_VALUE.yesterday}
                          excludeDates={[DATES_VALUE.allTIme]}
                        />
                      )}

                      {!use_date_range && (
                        <Field
                          variant="outlined"
                          name="report_title_date"
                          component={FormDatePicker}
                          label="Report title date"
                          size="small"
                          maxDate={moment_tz()}
                          datePickerProps={{ maxDateMessage: "" }}
                          disabled={use_date_range}
                        />
                      )}

                      {!use_date_range && (
                        <Button
                          disabled={!isValid || loading}
                          type="submit"
                          size="large"
                          sx={{ marginLeft: "20px" }}
                        >
                          Search
                        </Button>
                      )}
                    </Box>

                    <PassersReport />
                  </Grid>
                  <Grid item xs={1} sx={{ "&.MuiGrid-item": { paddingBottom: "15px" } }}></Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default AccountsSearch;
